import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

// DEVIATION FROM UPSTREAM: `logoNode` and `trailing`. Upstream takes the logo
// as an image src and renders nothing but `items` — this app's mark is an icon
// plus a serif wordmark, and its nav carries an auth control (Sign in, or
// Account with a PRO badge) that is not a nav link and must not be one.
const PillNav = ({
  logoNode,
  trailing,
  // DEVIATION FROM UPSTREAM: two more slots, both mobile-only. Upstream has one
  // row of pills and one drawer, and puts nothing beside the hamburger — so an
  // app whose nav carries controls (theme, auth) has to bury all of them behind
  // the menu. `mobileBar` keeps the ones that are one tap on desktop one tap on
  // a phone too; `mobileMenuFooter` takes the rest. Neither renders above `md`,
  // so the desktop bar is exactly what it was.
  mobileBar,
  mobileMenuFooter,
  pillGap = '3px',
  // DEVIATION FROM UPSTREAM: the current page gets its own fill rather than a
  // dot beneath the pill. Upstream draws that dot in --base, the colour of the
  // bar behind the pills — so wherever that bar matches the page background it
  // is invisible, and even when it shows, one small dot is a faint answer to
  // "where am I". Hover uses its own colour too, so it can preview the click
  // instead of being tied to the bar.
  activePillColor,
  activePillTextColor,
  hoverPillColor,
  pillOutlineColor,
  logo,
  logoAlt = 'Logo',
  items,
  activeHref,
  className = '',
  ease = 'power3.easeOut',
  baseColor = '#fff',
  pillColor = '#120F17',
  hoveredPillTextColor = '#120F17',
  pillTextColor,
  onMobileMenuClick,
  initialLoadAnimation = true
}) => {
  const resolvedPillTextColor = pillTextColor ?? baseColor;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const circleRefs = useRef([]);
  const tlRefs = useRef([]);
  const activeTweenRefs = useRef([]);
  const logoImgRef = useRef(null);
  const logoTweenRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navItemsRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const layout = () => {
      circleRefs.current.forEach(circle => {
        if (!circle?.parentElement) return;

        const pill = circle.parentElement;
        const rect = pill.getBoundingClientRect();
        const { width: w, height: h } = rect;
        const R = ((w * w) / 4 + h * h) / (2 * h);
        const D = Math.ceil(2 * R) + 2;
        const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
        const originY = D - delta;

        circle.style.width = `${D}px`;
        circle.style.height = `${D}px`;
        circle.style.bottom = `-${delta}px`;

        gsap.set(circle, {
          xPercent: -50,
          scale: 0,
          transformOrigin: `50% ${originY}px`
        });

        const label = pill.querySelector('.pill-label');
        const white = pill.querySelector('.pill-label-hover');

        if (label) gsap.set(label, { y: 0 });
        if (white) gsap.set(white, { y: h + 12, opacity: 0 });

        const index = circleRefs.current.indexOf(circle);
        if (index === -1) return;

        tlRefs.current[index]?.kill();
        const tl = gsap.timeline({ paused: true });

        tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' }, 0);

        if (label) {
          tl.to(label, { y: -(h + 8), duration: 2, ease, overwrite: 'auto' }, 0);
        }

        if (white) {
          gsap.set(white, { y: Math.ceil(h + 100), opacity: 0 });
          tl.to(white, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
        }

        tlRefs.current[index] = tl;
      });
    };

    layout();

    const onResize = () => layout();
    window.addEventListener('resize', onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(layout).catch(() => {});
    }

    const menu = mobileMenuRef.current;
    if (menu) {
      gsap.set(menu, { visibility: 'hidden', opacity: 0, scaleY: 1, y: 0 });
    }

    if (initialLoadAnimation) {
      const logo = logoRef.current;
      const navItems = navItemsRef.current;

      if (logo) {
        gsap.set(logo, { scale: 0 });
        gsap.to(logo, {
          scale: 1,
          duration: 0.6,
          ease
        });
      }

      if (navItems) {
        gsap.set(navItems, { width: 0, overflow: 'hidden' });
        gsap.to(navItems, {
          width: 'auto',
          duration: 0.6,
          ease
        });
      }
    }

    return () => window.removeEventListener('resize', onResize);
  }, [items, ease, initialLoadAnimation]);

  const handleEnter = i => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(tl.duration(), {
      duration: 0.3,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLeave = i => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(0, {
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLogoEnter = () => {
    const img = logoImgRef.current;
    if (!img) return;
    logoTweenRef.current?.kill();
    gsap.set(img, { rotate: 0 });
    logoTweenRef.current = gsap.to(img, {
      rotate: 360,
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  // DEVIATION FROM UPSTREAM: the open/close animation is split out of the
  // toggle handler so everything that closes the menu can drive it. Upstream
  // only ever calls this from the hamburger, and its menu links call a bare
  // `setIsMobileMenuOpen(false)` — which flips the state but never runs the
  // animation, so the panel is left sitting open, visible, over the page you
  // just navigated to.
  // The ref, not the state, is what the guard reads: this is called from event
  // listeners registered once per open, and a stale closure over the state
  // would let a second call re-run the animation from the top.
  const menuOpenRef = useRef(false);
  const setMenuOpen = useCallback(
    next => {
      if (menuOpenRef.current === next) return;
      menuOpenRef.current = next;
      setIsMobileMenuOpen(next);

      const hamburger = hamburgerRef.current;
      const menu = mobileMenuRef.current;

      if (hamburger) {
        const lines = hamburger.querySelectorAll('.hamburger-line');
        if (next) {
          gsap.to(lines[0], { rotation: 45, y: 3, duration: 0.3, ease });
          gsap.to(lines[1], { rotation: -45, y: -3, duration: 0.3, ease });
        } else {
          gsap.to(lines[0], { rotation: 0, y: 0, duration: 0.3, ease });
          gsap.to(lines[1], { rotation: 0, y: 0, duration: 0.3, ease });
        }
      }

      if (menu) {
        if (next) {
          gsap.set(menu, { visibility: 'visible' });
          gsap.fromTo(
            menu,
            { opacity: 0, y: 10, scaleY: 1 },
            {
              opacity: 1,
              y: 0,
              scaleY: 1,
              duration: 0.3,
              ease,
              transformOrigin: 'top center'
            }
          );
        } else {
          gsap.to(menu, {
            opacity: 0,
            y: 10,
            scaleY: 1,
            duration: 0.2,
            ease,
            transformOrigin: 'top center',
            onComplete: () => {
              gsap.set(menu, { visibility: 'hidden' });
            }
          });
        }
      }
    },
    [ease]
  );

  const toggleMobileMenu = () => {
    setMenuOpen(!isMobileMenuOpen);
    onMobileMenuClick?.();
  };

  // A menu that stays open after you have used it is the commonest way a phone
  // nav goes wrong, so every way out of it is wired up: picking a destination,
  // tapping the page behind, Escape, and — for the links that route to where
  // you already are, which fire no navigation — the route itself.
  const { pathname } = useLocation();
  useEffect(() => { setMenuOpen(false); }, [pathname, setMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onPointerDown = e => {
      if (mobileMenuRef.current?.contains(e.target)) return;
      if (hamburgerRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = e => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      hamburgerRef.current?.focus();
    };
    // Capture, so a tap that lands on something which stops propagation still
    // dismisses the menu.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobileMenuOpen, setMenuOpen]);

  const isExternalLink = href =>
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('//') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#');

  const isRouterLink = href => href && !isExternalLink(href);

  const cssVars = {
    '--base': baseColor,
    '--pill-bg': pillColor,
    '--hover-text': hoveredPillTextColor,
    '--pill-text': resolvedPillTextColor,
    '--nav-h': '42px',
    '--logo': '36px',
    '--pill-pad-x': '18px',
    // DEVIATION FROM UPSTREAM: a prop rather than the hardcoded 3px. That value
    // assumes the pills sit inside a contrasting bar, where near-touching reads
    // as one segmented control. On a plain paper background they are separate
    // objects and need air between them.
    '--pill-gap': pillGap,
    '--active-bg': activePillColor ?? pillColor,
    '--active-text': activePillTextColor ?? resolvedPillTextColor,
    '--hover-bg': hoverPillColor ?? baseColor,
    '--pill-outline': pillOutlineColor ?? 'transparent'
  };

  return (
    // DEVIATION FROM UPSTREAM: was `absolute top-[1em] ... md:w-auto
    // md:left-auto`, which floats the nav over the page and contributes no
    // height to whatever contains it. This app puts it inside a sticky header
    // with a border and a backdrop blur, and an absolutely positioned child left
    // that header 25px tall with the pills hanging below it. In flow instead, so
    // the bar wraps its own contents.
    <div className="relative z-[1000] w-full">
      <nav
        // DEVIATION: was `md:w-max` and `md:justify-start`, so on desktop the
        // whole nav shrank to its contents and packed into the left of the
        // container. Full width with justify-between instead: logo hard left,
        // controls hard right, pills taking the middle.
        // DEVIATION: the mobile `px-4` is gone. It assumed the nav owns the
        // page gutter, but here it sits inside a header that already applies
        // one — so on a phone the logo started 36px in while every heading
        // below it started at 20px, and the whole bar read as misaligned with
        // the page it belongs to. Desktop was already `md:px-0` and is
        // untouched.
        className={`w-full flex items-center justify-between box-border px-0 ${className}`}
        aria-label="Primary"
        style={cssVars}
      >
        {isRouterLink(items?.[0]?.href) ? (
          <Link
            to={items[0].href}
            aria-label="Home"
            onMouseEnter={handleLogoEnter}
            role="menuitem"
            ref={el => {
              logoRef.current = el;
            }}
            className={`rounded-full inline-flex items-center justify-center overflow-hidden ${
              logoNode ? 'px-1' : 'p-2'
            }`}
            style={{
              // A wordmark is wider than it is tall; only the image form wants
              // to be a circle.
              width: logoNode ? 'auto' : 'var(--nav-h)',
              height: 'var(--nav-h)',
              background: logoNode ? 'transparent' : 'var(--base, #000)'
            }}
          >
            {logoNode || (
              <img src={logo} alt={logoAlt} ref={logoImgRef} className="w-full h-full object-cover block" />
            )}
          </Link>
        ) : (
          <a
            href={items?.[0]?.href || '#'}
            aria-label="Home"
            onMouseEnter={handleLogoEnter}
            ref={el => {
              logoRef.current = el;
            }}
            className={`rounded-full inline-flex items-center justify-center overflow-hidden ${
              logoNode ? 'px-1' : 'p-2'
            }`}
            style={{
              // A wordmark is wider than it is tall; only the image form wants
              // to be a circle.
              width: logoNode ? 'auto' : 'var(--nav-h)',
              height: 'var(--nav-h)',
              background: logoNode ? 'transparent' : 'var(--base, #000)'
            }}
          >
            {logoNode || (
              <img src={logo} alt={logoAlt} ref={logoImgRef} className="w-full h-full object-cover block" />
            )}
          </a>
        )}

        <div
          ref={navItemsRef}
          // DEVIATION: flex-1 and justify-center so the pills centre in the
          // space left between the logo and the trailing controls, rather than
          // sitting immediately after the logo. Safe to stretch here because
          // this container's background is a variable the caller sets, and this
          // app sets it transparent — on upstream's dark default it would
          // stretch a visible slab across the middle.
          className="relative items-center rounded-full hidden md:flex md:flex-1 md:justify-center ml-2"
          style={{
            height: 'var(--nav-h)',
            background: 'var(--base, #000)'
          }}
        >
          <ul
            role="menubar"
            className="list-none flex items-stretch m-0 p-[3px] h-full"
            style={{ gap: 'var(--pill-gap)' }}
          >
            {items.map((item, i) => {
              const isActive = activeHref === item.href;

              const pillStyle = {
                background: isActive ? 'var(--active-bg)' : 'var(--pill-bg, #fff)',
                color: isActive ? 'var(--active-text)' : 'var(--pill-text, var(--base, #000))',
                // Outline, not border: it sits inside the box, so a pill does
                // not change size between its resting and selected states.
                boxShadow: isActive ? undefined : 'inset 0 0 0 1.5px var(--pill-outline)',
                paddingLeft: 'var(--pill-pad-x)',
                paddingRight: 'var(--pill-pad-x)'
              };

              const PillContent = (
                <>
                  <span
                    className="hover-circle absolute left-1/2 bottom-0 rounded-full z-[1] block pointer-events-none"
                    style={{
                      background: 'var(--hover-bg, var(--base, #000))',
                      willChange: 'transform'
                    }}
                    aria-hidden="true"
                    ref={el => {
                      circleRefs.current[i] = el;
                    }}
                  />
                  <span className="label-stack relative inline-block leading-[1] z-[2]">
                    <span
                      className="pill-label relative z-[2] inline-block leading-[1]"
                      style={{ willChange: 'transform' }}
                    >
                      {item.label}
                    </span>
                    <span
                      className="pill-label-hover absolute left-0 top-0 z-[3] inline-block"
                      style={{
                        color: 'var(--hover-text, #fff)',
                        willChange: 'transform, opacity'
                      }}
                      aria-hidden="true"
                    >
                      {item.label}
                    </span>
                  </span>
                  {isActive && !activePillColor && (
                    <span
                      className="absolute left-1/2 -bottom-[6px] -translate-x-1/2 w-3 h-3 rounded-full z-[4]"
                      style={{ background: 'var(--base, #000)' }}
                      aria-hidden="true"
                    />
                  )}
                </>
              );

              const basePillClasses =
                'relative overflow-hidden inline-flex items-center justify-center h-full no-underline rounded-full box-border font-semibold text-[16px] leading-[0] uppercase tracking-[0.2px] whitespace-nowrap cursor-pointer px-0';

              return (
                <li key={item.href} role="none" className="flex h-full">
                  {isRouterLink(item.href) ? (
                    <Link
                      role="menuitem"
                      to={item.href}
                      className={basePillClasses}
                      style={pillStyle}
                      aria-label={item.ariaLabel || item.label}
                      onMouseEnter={() => handleEnter(i)}
                      onMouseLeave={() => handleLeave(i)}
                    >
                      {PillContent}
                    </Link>
                  ) : (
                    <a
                      role="menuitem"
                      href={item.href}
                      className={basePillClasses}
                      style={pillStyle}
                      aria-label={item.ariaLabel || item.label}
                      onMouseEnter={() => handleEnter(i)}
                      onMouseLeave={() => handleLeave(i)}
                    >
                      {PillContent}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {trailing && <div className="ml-3 hidden md:flex items-center">{trailing}</div>}

        {/* Everything from here down is `md:hidden`, so none of it exists on
            desktop and none of it can change what the desktop bar looks like. */}
        <div className="flex items-center gap-2 md:hidden">
          {mobileBar}

          <button
            ref={hamburgerRef}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="pillnav-mobile-menu"
            // DEVIATION FROM UPSTREAM: the button paints itself like the pills
            // beside it instead of like the bar behind it. Upstream fills it
            // with `--base` and draws the lines in `--pill-bg`, which assumes
            // the two always contrast. Here --base is transparent and --pill-bg
            // is the surface colour, so the lines came out surface-on-surface —
            // white bars on a white header in light mode, slate on slate in
            // dark — and the only way to reach the nav on a phone was invisible
            // in both themes. Lines in --pill-text on a --pill-bg field with the
            // pills' own outline: same object, same colours, visible by
            // construction rather than by coincidence.
            className="rounded-full border-0 flex flex-col items-center justify-center gap-1 cursor-pointer p-0 relative transition-transform duration-150 ease-out active:scale-[0.94]"
            style={{
              width: 'var(--nav-h)',
              height: 'var(--nav-h)',
              background: 'var(--pill-bg, #fff)',
              boxShadow: 'inset 0 0 0 1.5px var(--pill-outline)'
            }}
          >
            <span
              className="hamburger-line w-[18px] h-[2px] rounded origin-center"
              style={{ background: 'var(--pill-text, #000)' }}
            />
            <span
              className="hamburger-line w-[18px] h-[2px] rounded origin-center"
              style={{ background: 'var(--pill-text, #000)' }}
            />
          </button>
        </div>
      </nav>

      {/* DEVIATION FROM UPSTREAM: the panel is opaque and the items are flat.
          Upstream paints the panel in --base and each item in --pill-bg, which
          only reads as a menu where those two contrast. With --base transparent
          the panel was a hole: the page scrolled visibly between the items and
          the whole thing sat unreadable on top of the hero. Painting the panel
          in --pill-bg and letting the items sit on it as plain rows is the same
          arrangement the desktop bar uses, one surface with the current page
          filled in.

          The per-item mouse handlers are gone with it. They swapped the item to
          `--base` on --hover-text, i.e. transparent behind paper-coloured text;
          a phone fires mouseenter on tap and leaves it applied, so choosing a
          destination turned that row invisible on the way out. Nothing here is
          hover state now — a press is what a touch device actually has. */}
      <div
        ref={mobileMenuRef}
        id="pillnav-mobile-menu"
        // `invisible` in the class rather than only in the mount effect, which
        // is a useEffect and so runs after the first paint. Upstream could get
        // away with that — its panel was transparent — but an opaque one shows
        // as a full menu flashing open on every load. gsap writes visibility
        // inline from here on, which outranks the class either way.
        className="md:hidden invisible absolute top-[calc(var(--nav-h)+10px)] left-0 right-0 overflow-hidden rounded-2xl border border-line/10 shadow-[0_12px_40px_rgb(2_6_23_/_0.18)] z-[998] origin-top"
        style={{
          ...cssVars,
          background: 'var(--pill-bg, #fff)'
        }}
      >
        <ul className="list-none m-0 p-2 flex flex-col gap-1">
          {items.map(item => {
            const isActive = activeHref === item.href;
            const itemStyle = {
              background: isActive ? 'var(--active-bg)' : 'transparent',
              color: isActive ? 'var(--active-text)' : 'var(--pill-text, #000)'
            };

            // min-h-11 is the whole point of the row on a phone: 44px is the
            // smallest target a thumb hits reliably, and the old py-3 on a
            // 16px line came in a couple of pixels under it.
            const linkClasses =
              'flex min-h-11 items-center rounded-xl px-4 text-[16px] font-semibold transition-transform duration-150 ease-out active:scale-[0.98]';

            return (
              <li key={item.href}>
                {isRouterLink(item.href) ? (
                  <Link
                    to={item.href}
                    className={linkClasses}
                    style={itemStyle}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    className={linkClasses}
                    style={itemStyle}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            );
          })}
          {(mobileMenuFooter ?? trailing) && (
            <li className="mt-1 flex items-center gap-2 border-t border-line/10 px-2 pb-1 pt-3">
              {mobileMenuFooter ?? trailing}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PillNav;
